import { createContext, useCallback, useContext, useEffect, useState } from "react";

const StudentAuthContext = createContext(null);

const STORAGE_KEY = "puregloss_student_token";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const StudentAuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [profile, setProfile] = useState(null);  // { student, modules, progress_percent }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMe = useCallback(async (tk) => {
    if (!tk) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/academy/me`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (res.status === 401 || res.status === 403) {
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        setToken(null);
        setProfile(null);
        setError("expired");
        return null;
      }
      if (!res.ok) throw new Error("fetch failed");
      const data = await res.json();
      setProfile(data);
      return data;
    } catch (e) {
      setError(e.message || "error");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchMe(token);
  }, [token, fetchMe]);

  const login = useCallback(async (email, password) => {
    setError(null);
    const res = await fetch(`${BACKEND_URL}/api/academy/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = typeof data.detail === "string" ? data.detail : "login_failed";
      throw new Error(detail);
    }
    try { localStorage.setItem(STORAGE_KEY, data.token); } catch { /* ignore */ }
    setToken(data.token);
    return data;
  }, []);

  const logout = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setToken(null);
    setProfile(null);
  }, []);

  const setModuleProgress = useCallback(async (moduleId, completed) => {
    if (!token) return;
    await fetch(`${BACKEND_URL}/api/academy/me/progress`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ module_id: moduleId, completed }),
    });
    // refresh
    fetchMe(token);
  }, [token, fetchMe]);

  return (
    <StudentAuthContext.Provider value={{
      token, profile, loading, error,
      login, logout, refresh: () => fetchMe(token),
      setModuleProgress,
    }}>
      {children}
    </StudentAuthContext.Provider>
  );
};

export const useStudentAuth = () => {
  const ctx = useContext(StudentAuthContext);
  if (!ctx) throw new Error("useStudentAuth must be used inside StudentAuthProvider");
  return ctx;
};
